import React, { useState, useEffect, useRef } from "react";
import {
  AppBar, Tabs, Tab, Box, Container, Typography, Paper, Button, TextField
} from "@mui/material";
import RotationTable from "../RotationTable";
import PortfolioChart from "../PortfolioChart";
import {
  getLatestRotation, getRotationDates, getRotationByDate,
  getSippFunds, addSippFunds, removeSippFunds,
  getIsaFunds, addIsaFunds, removeIsaFunds,
  getTradingFunds, addTradingFunds, removeTradingFunds,
  getSippChart, getIsaChart, getTradingChart, getRotationChart,
  getFundHistory, getCurrentUser,
  addFundToRotation, removeFundFromRotation,
  triggerRotationRun, triggerBackfill, previewRotation
} from "../../services/api";
export default function Dashboard() {
  const [activeTab, setActiveTab] = useState(0);
  const [userRole, setUserRole] = useState("general");
  const [showAddFund, setShowAddFund] = useState(false);
  const [newTicker, setNewTicker] = useState("");
  const [newFundName, setNewFundName] = useState("");
  const [busy, setBusy] = useState(""); // "", "rotation", "backfill"
  // ---- preview state (Update Rotation to Now) ----
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewUsed, setPreviewUsed] = useState(false); // once per page open
  // ---- data states ----
  const [rotationData, setRotationData] = useState([]);
  const [rotationRuns, setRotationRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null); // ISO date string, null = Latest
  const [sippFunds, setSippFunds] = useState([]);
  const [isaFunds, setIsaFunds] = useState([]);
  const [tradingFunds, setTradingFunds] = useState([]);
  // ---- chart data states ----
  const [rotationChartHistory, setRotationChartHistory] = useState([]);
  const [rotationChartAverage, setRotationChartAverage] = useState([]);
  const [sippChartHistory, setSippChartHistory] = useState([]);
  const [sippChartAverage, setSippChartAverage] = useState([]);
  const [isaChartHistory, setIsaChartHistory] = useState([]);
  const [isaChartAverage, setIsaChartAverage] = useState([]);
  const [tradingChartHistory, setTradingChartHistory] = useState([]);
  const [tradingChartAverage, setTradingChartAverage] = useState([]);
  // ---- loading states (so "no data" is NOT shown while a fetch is in flight) ----
  const [rotationLoading, setRotationLoading] = useState(true);
  const [sippLoading, setSippLoading] = useState(true);
  const [isaLoading, setIsaLoading] = useState(true);
  const [tradingLoading, setTradingLoading] = useState(true);
  const [rotationChartLoading, setRotationChartLoading] = useState(true);
  const [sippChartLoading, setSippChartLoading] = useState(true);
  const [isaChartLoading, setIsaChartLoading] = useState(true);
  const [tradingChartLoading, setTradingChartLoading] = useState(true);
  // Chart selection per tab — single source of truth, owned by Dashboard.
  const [rotationChartIds, setRotationChartIds] = useState([]);
  const [sippChartIds, setSippChartIds] = useState([]);
  const [isaChartIds, setIsaChartIds] = useState([]);
  const [tradingChartIds, setTradingChartIds] = useState([]);
  const [rotationTableClearSignal, setRotationTableClearSignal] = useState(0);
  const [fundHistoryMap, setFundHistoryMap] = useState({});
  const inFlightHistories = useRef(new Set());
  const recommendedHoldings = (rotationData || [])
    .filter(r => r.is_recommended === true)
    .sort((a, b) => (b.final_score ?? 0) - (a.final_score ?? 0));
  // Generic toggle helper — updates a tab's selection without duplicates.
  const toggleIdIn = (setter) => (fundId, checked) => {
    setter(prev => checked
      ? (prev.includes(fundId) ? prev : [...prev, fundId])
      : prev.filter(id => id !== fundId));
  };
  const handleRotationChartToggle = toggleIdIn(setRotationChartIds);
  const handleSippChartToggle = toggleIdIn(setSippChartIds);
  const handleIsaChartToggle = toggleIdIn(setIsaChartIds);
  const handleTradingChartToggle = toggleIdIn(setTradingChartIds);
  // Default: Rotation tab -> only the top-N recommended funds checked (T5 = 4).
  const applyRotationChartSelection = (results) => {
    setRotationChartIds(
      (results || [])
        .filter(r => r.is_recommended === true)
        .sort((a, b) => (b.final_score ?? 0) - (a.final_score ?? 0))
        .slice(0, 4)
        .map(r => r.fund_id)
    );
  };
  // ---- loaders (defaults applied here, when data loads) ----
  const loadRotation = async () => {
    setRotationLoading(true);
    try {
      const res = await getLatestRotation();
      const results = res.data.results || [];
      setRotationData(results);
      applyRotationChartSelection(results);
    } catch (err) { console.error("loadRotation failed", err); }
    finally { setRotationLoading(false); }
  };
  const loadRotationRuns = async () => {
    try {
      const res = await getRotationDates();
      setRotationRuns(res.data || []);
    } catch (err) { console.error("loadRotationRuns failed", err); }
  };
  const loadSipp = async (runDate) => {
    setSippLoading(true);
    try {
      const res = await getSippFunds(runDate || null);
      const funds = res.data || [];
      setSippFunds(funds);
      setSippChartIds(funds.map(f => f.fund_id));           // default: all checked
      funds.forEach(f => ensureFundHistory(f.fund_id));
    } catch (err) { console.error("loadSipp failed", err); }
    finally { setSippLoading(false); }
  };
  const loadIsa = async (runDate) => {
    setIsaLoading(true);
    try {
      const res = await getIsaFunds(runDate || null);
      const funds = res.data || [];
      setIsaFunds(funds);
      setIsaChartIds(funds.map(f => f.fund_id));            // default: all checked
      funds.forEach(f => ensureFundHistory(f.fund_id));
    } catch (err) { console.error("loadIsa failed", err); }
    finally { setIsaLoading(false); }
  };
  const loadTrading = async (runDate) => {
    setTradingLoading(true);
    try {
      const res = await getTradingFunds(runDate || null);
      const funds = res.data || [];
      setTradingFunds(funds);
      setTradingChartIds(funds.map(f => f.fund_id));        // default: all checked
      funds.forEach(f => ensureFundHistory(f.fund_id));
    } catch (err) { console.error("loadTrading failed", err); }
    finally { setTradingLoading(false); }
  };
  const loadRotationChart = async () => {
    setRotationChartLoading(true);
    try {
      const res = await getRotationChart();
      setRotationChartHistory(res.data.history || []);
      setRotationChartAverage(res.data.average || []);
    } catch (err) { console.error("loadRotationChart failed", err); }
    finally { setRotationChartLoading(false); }
  };
  const loadSippChart = async () => {
    setSippChartLoading(true);
    try {
      const res = await getSippChart();
      setSippChartHistory(res.data.history || []);
      setSippChartAverage(res.data.average || []);
    } catch (err) { console.error("loadSippChart failed", err); }
    finally { setSippChartLoading(false); }
  };
  const loadIsaChart = async () => {
    setIsaChartLoading(true);
    try {
      const res = await getIsaChart();
      setIsaChartHistory(res.data.history || []);
      setIsaChartAverage(res.data.average || []);
    } catch (err) { console.error("loadIsaChart failed", err); }
    finally { setIsaChartLoading(false); }
  };
  const loadTradingChart = async () => {
    setTradingChartLoading(true);
    try {
      const res = await getTradingChart();
      setTradingChartHistory(res.data.history || []);
      setTradingChartAverage(res.data.average || []);
    } catch (err) { console.error("loadTradingChart failed", err); }
    finally { setTradingChartLoading(false); }
  };
  const ensureFundHistory = (fundId) => {
    if (fundHistoryMap[fundId]) return;
    if (inFlightHistories.current.has(fundId)) return;
    inFlightHistories.current.add(fundId);
    getFundHistory(fundId)
      .then(res => setFundHistoryMap(prev => ({ ...prev, [fundId]: res.data || [] })))
      .catch(err => console.error("Failed to load fund history", err))
      .finally(() => inFlightHistories.current.delete(fundId));
  };
  // ---- portfolio actions ----
  const handleAddToSipp = async (ids) => {
    try {
      await addSippFunds(ids);
      setRotationTableClearSignal(prev => prev + 1);
      await loadSipp(selectedRun);
    } catch (err) { console.error("Add to SIPP failed", err); }
  };
  const handleRemoveFromSipp = async (ids) => {
    try {
      await removeSippFunds(ids);
      await loadSipp(selectedRun);
    } catch (err) { console.error("Remove from SIPP failed", err); }
  };
  const handleAddToIsa = async (ids) => {
    try {
      await addIsaFunds(ids);
      setRotationTableClearSignal(prev => prev + 1);
      await loadIsa(selectedRun);
    } catch (err) { console.error("Add to ISA failed", err); }
  };
  const handleRemoveFromIsa = async (ids) => {
    try {
      await removeIsaFunds(ids);
      await loadIsa(selectedRun);
    } catch (err) { console.error("Remove from ISA failed", err); }
  };
  const handleAddToTrading = async (ids) => {
    try {
      await addTradingFunds(ids);
      setRotationTableClearSignal(prev => prev + 1);
      await loadTrading(selectedRun);
    } catch (err) { console.error("Add to Trading failed", err); }
  };
  const handleRemoveFromTrading = async (ids) => {
    try {
      await removeTradingFunds(ids);
      await loadTrading(selectedRun);
    } catch (err) { console.error("Remove from Trading failed", err); }
  };
  // ---- admin actions ----
  const handleAddFund = async () => {
    if (!newTicker || !newFundName) return;
    try {
      await addFundToRotation(newTicker, newFundName);
      setNewTicker(""); setNewFundName(""); setShowAddFund(false);
      await loadRotation();
    } catch (err) { console.error("Add fund failed", err); }
  };
  const handleRemoveFund = async (fundId) => {
    try {
      await removeFundFromRotation(fundId);
      await loadRotation();
    } catch (err) { console.error("Remove fund failed", err); }
  };
  const handleRunRotation = async () => {
    setBusy("rotation");
    try {
      await triggerRotationRun();
      await Promise.all([loadRotation(), loadRotationRuns(), loadRotationChart()]);
    } catch (err) { console.error("handleRunRotation failed", err); }
    finally { setBusy(""); }
  };
  const handleBackfill = async () => {
    const ok = window.confirm(
      "This will DELETE all rotation history and rebuild it with the current engine. " +
      "This can take a few minutes and may briefly show no data. Continue?"
    );
    if (!ok) return;
    setBusy("backfill");
    try {
      await triggerBackfill();
      await Promise.all([loadRotation(), loadRotationRuns(), loadRotationChart()]);
    } catch (err) { console.error("handleBackfill failed", err); }
    finally { setBusy(""); }
  };
  // ---- preview action (Update Rotation to Now) — all users, once per session ----
  const handlePreviewRotation = async () => {
    setPreviewBusy(true);
    try {
      await previewRotation();
      await Promise.all([loadRotation(), loadRotationRuns(), loadRotationChart()]);
      setPreviewUsed(true);
    } catch (err) { console.error("handlePreviewRotation failed", err); }
    finally { setPreviewBusy(false); }
  };
  // ---- date selector ----
  const handleDateChange = async (e) => {
    const runDate = e.target.value || null;
    setSelectedRun(runDate);
    try {
      if (runDate) {
        const res = await getRotationByDate(runDate);
        const results = res.data.results || [];
        setRotationData(results);
      } else {
        await loadRotation();
      }
      await Promise.all([loadSipp(runDate), loadIsa(runDate), loadTrading(runDate)]);
    } catch (err) { console.error("handleDateChange failed", err); }
  };
  // ---- tab switching ----
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 0) { loadRotation(); loadRotationChart(); }
    else if (newValue === 1) { loadSipp(selectedRun); loadSippChart(); }
    else if (newValue === 2) { loadIsa(selectedRun); loadIsaChart(); }
    else if (newValue === 3) { loadTrading(selectedRun); loadTradingChart(); }
  };
  // ---- mount ----
  useEffect(() => {
    loadRotationRuns();
    loadRotation();
    loadRotationChart();
    getCurrentUser()
      .then(res => setUserRole(res.data.role || "general"))
      .catch(() => setUserRole("general"));
  }, []);
  const isAdmin = userRole === "admin";
  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>SIPP Fund Prediction Dashboard</Typography>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Select Rotation Date</Typography>
        <select value={selectedRun || ""} onChange={handleDateChange} style={{ padding: "8px", fontSize: "16px" }}>
          <option value="">Latest</option>
          {rotationRuns.map(date => (
            <option key={date} value={date}>{date}</option>
          ))}
        </select>
        {selectedRun && (
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Showing Rotation Results for {selectedRun}. The charts below show overall history.
          </Typography>
        )}
      </Paper>
      <AppBar position="static" color="default">
        <Tabs value={activeTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary" variant="fullWidth">
          <Tab label="Rotation" /><Tab label="SIPP" /><Tab label="ISA" /><Tab label="Trading" />
        </Tabs>
      </AppBar>
      <Box sx={{ mt: 4 }}>
        {activeTab === 0 && (
          <>
            {recommendedHoldings.length > 0 && (
              <Paper sx={{ p: 2, mb: 2, borderLeft: "6px solid #2e7d32" }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Current Holdings ({recommendedHoldings.length})</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                  {recommendedHoldings.map(r => (
                    <Box key={r.fund_id || r.ticker} sx={{ p: 1.5, borderRadius: 1, bgcolor: "success.light", minWidth: 220 }}>
                      <a href={`https://uk.finance.yahoo.com/quote/${encodeURIComponent(r.ticker || "")}`}
                        target="_blank" rel="noopener noreferrer"
                        style={{ textDecoration: "none", color: "#004d40", fontWeight: 600 }}>
                        {r.name || r.ticker}
                      </a>
                      <Typography variant="caption" display="block">
                        score {(r.final_score ?? 0).toFixed(2)} · {r.final_decision}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}
            {/* Update Rotation to Now — available to ALL users, once per page open */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                <Button variant="contained" color="success"
                  onClick={handlePreviewRotation}
                  disabled={previewUsed || previewBusy || busy !== ""}>
                  {previewBusy ? "Updating..." : previewUsed ? "Previewed Today" : "Update Rotation to Now"}
                </Button>
                {previewUsed && (
                  <Typography variant="caption">
                    Showing today's preview (computed live, NOT saved to database).
                  </Typography>
                )}
              </Box>
            </Paper>
            {isAdmin && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                  <Button variant="contained" color="primary" onClick={handleRunRotation} disabled={busy !== ""}>
                    {busy === "rotation" ? "Running..." : "Run Rotation Now"}
                  </Button>
                  <Button variant="contained" color="warning" onClick={handleBackfill} disabled={busy !== ""}>
                    {busy === "backfill" ? "Rebuilding..." : "Re-backfill History"}
                  </Button>
                  <Button variant="outlined" onClick={() => setShowAddFund(!showAddFund)}>
                    {showAddFund ? "Cancel" : "Add Fund"}
                  </Button>
                  {showAddFund && (
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <TextField size="small" label="Ticker" value={newTicker} onChange={(e) => setNewTicker(e.target.value)} />
                      <TextField size="small" label="Fund Name" value={newFundName} onChange={(e) => setNewFundName(e.target.value)} />
                      <Button variant="contained" onClick={handleAddFund}>Add</Button>
                    </Box>
                  )}
                </Box>
              </Paper>
            )}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Rotation Results</Typography>
              <RotationTable
                rows={rotationData}
                loading={rotationLoading}
                onAddToSipp={handleAddToSipp}
                onAddToIsa={handleAddToIsa}
                onAddToTrading={handleAddToTrading}
                onChartToggle={handleRotationChartToggle}
                chartSelectedIds={rotationChartIds}
                clearSelectionSignal={rotationTableClearSignal}
                isAdmin={isAdmin}
                onRemoveFund={handleRemoveFund}
              />
            </Paper>
            <PortfolioChart
              title="Rotation Performance"
              history={rotationChartHistory}
              average={rotationChartAverage}
              selectedFundIds={rotationChartIds}
              loading={rotationChartLoading}
            />
          </>
        )}
        {activeTab === 1 && (
          <>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>SIPP Portfolio</Typography>
              <RotationTable
                rows={sippFunds}
                mode="portfolio"
                loading={sippLoading}
                onRemoveSelected={handleRemoveFromSipp}
                onChartToggle={handleSippChartToggle}
                chartSelectedIds={sippChartIds}
                clearSelectionSignal={rotationTableClearSignal}
              />
            </Paper>
            <PortfolioChart
              title="SIPP Performance"
              history={sippChartHistory}
              average={sippChartAverage}
              selectedFundIds={sippChartIds}
              loading={sippChartLoading}
            />
          </>
        )}
        {activeTab === 2 && (
          <>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>ISA Portfolio</Typography>
              <RotationTable
                rows={isaFunds}
                mode="portfolio"
                loading={isaLoading}
                onRemoveSelected={handleRemoveFromIsa}
                onChartToggle={handleIsaChartToggle}
                chartSelectedIds={isaChartIds}
                clearSelectionSignal={rotationTableClearSignal}
              />
            </Paper>
            <PortfolioChart
              title="ISA Performance"
              history={isaChartHistory}
              average={isaChartAverage}
              selectedFundIds={isaChartIds}
              loading={isaChartLoading}
            />
          </>
        )}
        {activeTab === 3 && (
          <>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Trading Portfolio</Typography>
              <RotationTable
                rows={tradingFunds}
                mode="portfolio"
                loading={tradingLoading}
                onRemoveSelected={handleRemoveFromTrading}
                onChartToggle={handleTradingChartToggle}
                chartSelectedIds={tradingChartIds}
                clearSelectionSignal={rotationTableClearSignal}
              />
            </Paper>
            <PortfolioChart
              title="Trading Performance"
              history={tradingChartHistory}
              average={tradingChartAverage}
              selectedFundIds={tradingChartIds}
              loading={tradingChartLoading}
            />
          </>
        )}
      </Box>
    </Container>
  );
}
